import { Box, LinearProgress } from "@mui/material";
import LogoSvg from "../assets/common/7thGear-logo.svg";
import { LoginWrapper, AuthWrapper } from "./LoginStyles";
import GoogleIcon from "../assets/google.svg";
import MicrosoftIcon from "../assets/microsoft.svg";
import { useEffect, useRef, useState } from "react";
import useDisplaySnackbar from "../utils/useDisplaySnackbar";

import "../styles/common.css";

const HIDE_MICROSOFT_DOMAIN = "7thgear.highradius.com";

type SigningInProvider = "microsoft" | "google" | null;

const LoginPage = () => {
  const [hideMicrosoftLogin, setHideMicrosoftLogin] = useState(false);
  const [signingInProvider, setSigningInProvider] = useState<SigningInProvider>(null);
  const { showSnackbar } = useDisplaySnackbar();
  const signingInRef = useRef(false);

  const isSigningIn = signingInProvider !== null;
  const isMicrosoftSigningIn = signingInProvider === "microsoft";
  const isGoogleSigningIn = signingInProvider === "google";

  useEffect(() => {
    // Clear meeting prefs once on mount (not during render)
    localStorage.removeItem("startEndMeetingTime");
    localStorage.removeItem("selectedDate");
    localStorage.removeItem("view");

    // Hide Microsoft login only on the exact highradius domain
    if (window.location.hostname === HIDE_MICROSOFT_DOMAIN) {
      setHideMicrosoftLogin(true);
    }

    // Check login error from URL
    const params = new URLSearchParams(window.location.search);
    const error = params.get("error");

    if (error === "callback_failed") {
      showSnackbar("error", "Something went wrong. Please try again.");

      // Stop loader & allow retry
      signingInRef.current = false;
      setSigningInProvider(null);

      // Remove error from URL so snackbar shows only once
      params.delete("error");
      const newUrl =
        window.location.pathname +
        (params.toString() ? `?${params.toString()}` : "");

      window.history.replaceState({}, "", newUrl);
    }
  }, []);

  useEffect(() => {
    const resetLoginState = () => {
      setSigningInProvider(null);
      signingInRef.current = false;
    };

    // Handles browser back/forward cache restore
    window.addEventListener("pageshow", resetLoginState);

    // Handles history navigation (Back / Forward)
    window.addEventListener("popstate", resetLoginState);

    return () => {
      window.removeEventListener("pageshow", resetLoginState);
      window.removeEventListener("popstate", resetLoginState);
    };
  }, []);

  const BACKEND_URL = import.meta.env.VITE_JAVA_SERVICE_ACCOUNTS_BASE;

  /**
   * Validate the `continue` query param to prevent open-redirect.
   * Only allow relative paths (e.g. "/meetings") — reject absolute URLs,
   * protocol-relative URLs, and javascript: URIs.
   */
  const getSafeContinueUrl = (): string => {
    const params = new URLSearchParams(window.location.search);
    const continueUrl = params.get("continue") || "/";

    if (
      continueUrl.startsWith("http://") ||
      continueUrl.startsWith("https://") ||
      continueUrl.startsWith("//") ||
      continueUrl.startsWith("javascript:") ||
      continueUrl.startsWith("data:")
    ) {
      return "/";
    }

    return continueUrl;
  };

  const handleMicrosoftSignIn = () => {
    // Hard guard against multiple clicks
    if (signingInRef.current) return;

    try {
      signingInRef.current = true;
      setSigningInProvider("microsoft");

      const continueUrl = getSafeContinueUrl();

      // Clear storage BEFORE redirecting (consistent with Google flow)
      localStorage.removeItem("TaskPreferences");

      const loginUrl = `${BACKEND_URL}auth/login?provider=Microsoft&redirect=${encodeURIComponent(continueUrl)}`;
      window.location.href = loginUrl;
    } catch (error) {
      console.error("Error signing in with Microsoft:", error);
      signingInRef.current = false;
      setSigningInProvider(null);
    }
  };

  const handleGoogleSignIn = () => {
    // Hard guard against multiple clicks
    if (signingInRef.current) return;

    try {
      signingInRef.current = true;
      setSigningInProvider("google");

      const continueUrl = getSafeContinueUrl();

      localStorage.removeItem("TaskPreferences");

      const loginUrl = `${BACKEND_URL}auth/login?provider=Google&redirect=${encodeURIComponent(
        continueUrl
      )}`;

      window.location.href = loginUrl;
    } catch (error) {
      console.error("Error signing in with Google:", error);
      signingInRef.current = false;
      setSigningInProvider(null);
    }
  };

  return (
    <LoginWrapper>
      <Box
        className={`login-logo-container${hideMicrosoftLogin ? " login-logo-container--hide-microsoft" : ""}`}
      >
        <img
          className="login-logo"
          src={LogoSvg}
          alt="7thGear"
        />
      </Box>
      <AuthWrapper>
        {!hideMicrosoftLogin && (
          <Box className="login-provider-box login-provider-box--microsoft">
            <Box className="login-provider-inner">
              {/* Loader attached to button — only when Microsoft is clicked */}
              {isMicrosoftSigningIn && (
                <LinearProgress className="login-loader" />
              )}

              <button
                className={`loginPage-button${isMicrosoftSigningIn ? " loginPage-button--loading" : ""}`}
                onClick={handleMicrosoftSignIn}
                disabled={isSigningIn}
              >
                <img
                  className="loginPage-button__icon"
                  src={MicrosoftIcon}
                  alt="Microsoft"
                />
                {isMicrosoftSigningIn ? "Signing in..." : "Sign in with Microsoft"}
              </button>
            </Box>
          </Box>
        )}
        <Box className="login-provider-box login-provider-box--google">
          <Box className="login-provider-inner">
            {/* Loader attached to button — only when Google is clicked */}
            {isGoogleSigningIn && (
              <LinearProgress className="login-loader" />
            )}

            <button
              className={`loginPage-button${isGoogleSigningIn ? " loginPage-button--loading" : ""}`}
              onClick={handleGoogleSignIn}
              disabled={isSigningIn}
            >
              <img
                className="loginPage-button__icon"
                src={GoogleIcon}
                alt="Google"
              />

              {isGoogleSigningIn ? "Signing in..." : "Sign in with Google"}
            </button>
          </Box>
        </Box>
      </AuthWrapper>
    </LoginWrapper>
  );
};

export default LoginPage;